import React, { useEffect, useState, useCallback } from "react";
import {
    Tag, Typography, Button, Modal, Card, Divider,
    message, Avatar, List, Descriptions, Spin
} from "antd";
import { EyeOutlined, SwapOutlined, ReloadOutlined } from "@ant-design/icons";
import DataTable from "../component/ui/DataTable";
import OrdersServ from "../service/OrdersServ";
import Breadcrumbs from "../component/Breadcrumbs";
import { usePreference } from "../context/PreferenceContext";
import { formatDateTime } from "../util/dateUtils";
import "../css/orders.css";
import EmptyState from "../util/EmptyState";
import { getImageUrl } from "../component/SafeImage";
import { useLocation } from "react-router-dom";

const { Text } = Typography;

const STATUS_COLORS = {
    RETURN_REQUESTED: "orange",
    RETURN_APPROVED_BY_ADMIN: "blue",
    RETURN_REJECTED_BY_ADMIN: "red",
    RETURN_ACCEPTED_BY_SELLER: "purple",
    RETURN_REJECTED_BY_SELLER: "red",
    RETURN_PICKUP_ASSIGNED: "cyan",
    RETURN_PICKUP_ACCEPTED: "cyan",
    RETURN_PICKED_UP: "purple",
    RETURN_PICKUP_REJECTED: "red",
    RETURN_IN_TRANSIT: "blue",
    RETURN_DELIVERED_TO_SELLER: "blue",
    RETURN_RECEIVED_VERIFIED: "green",
    RETURN_RECEIVED_FAILED: "red",
    REFUND_PROCESSING: "blue",
    REFUND_COMPLETED: "green",
    
    requested: "orange",
    approved: "blue",
    rejected: "red",
    picked_up: "purple",
    refund_completed: "green",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} • ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const BuyerReturnRequests = () => {
    const { t, formatPrice } = usePreference();
    const location = useLocation();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchRequests = useCallback(async (showLoading = true) => {
        try {
            if (showLoading && requests.length === 0) setLoading(true);
            const cached = localStorage.getItem('cached_buyer_returns');
            if (cached && showLoading) {
                try {
                    const parsed = JSON.parse(cached);
                    setRequests(parsed);
                    setLoading(false); // Stop skeleton immediately
                } catch(e) {}
            }

            const res = await OrdersServ.getMyReturns();
            
            // Safely extract the array whether it's in res, res.data, or res.data.data
            let all = [];
            if (Array.isArray(res)) {
                all = res;
            } else if (res?.data && Array.isArray(res.data)) {
                all = res.data;
            } else if (res?.data?.data && Array.isArray(res.data.data)) {
                all = res.data.data;
            } else if (res?.returns && Array.isArray(res.returns)) {
                all = res.returns;
            }
            
            // Only filter out "cancel" type, but keep "return" type (including refund_completed)
            // so the user can see their full return history
            const returns = all.filter((r) => r.type === "return" || !r.type);
            const sorted = returns.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            localStorage.setItem('cached_buyer_returns', JSON.stringify(sorted));
            setRequests(sorted);
        } catch (error) {
            console.error("Fetch Error:", error);
            message.error("Failed to load return requests");
        } finally {
            setLoading(false);
        }
    }, [requests.length]);

    useEffect(() => {
        fetchRequests(true);
    }, []);

    useEffect(() => {
        if (location.state?.highlightOrderId && requests.length > 0) {
            const reqToOpen = requests.find(r => r.order?._id === location.state.highlightOrderId || r.order?.orderId === location.state.highlightOrderId);
            if (reqToOpen && (!selectedRequest || selectedRequest._id !== reqToOpen._id)) {
                handleViewDetails(reqToOpen);
                // Clear the state so it doesn't reopen on refresh
                window.history.replaceState({}, document.title);
            }
        }
    }, [location.state, requests]);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setIsModalOpen(true);
    };

    const StatusTag = ({ status }) => {
        const readableStatus = status
            ? status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
            : "";
        const normalizedStatus = (status || "").trim().toUpperCase();
        return (
            <Tag color={STATUS_COLORS[normalizedStatus] || "default"} style={{ borderRadius: 6, fontWeight: 600 }}>
                {readableStatus}
            </Tag>
        );
    };

    const columns = [
        {
            title: t("product", "Product"),
            key: "product",
            render: (_, record) => {
                const displayItems = record.returnItems && record.returnItems.length > 0 ? record.returnItems : (record.items || []);
                const firstItem = displayItems[0];
                const moreCount = (displayItems.length || 1) - 1;
                const name = firstItem?.productName || firstItem?.name || firstItem?.product?.name || "—";
                const img = firstItem?.productImage || firstItem?.imageUrl || firstItem?.product?.imageUrl;
                return (
                    <div className="pcu-flex-center-y pcu-gap-10">
                        <Avatar shape="square" size={56} src={getImageUrl(img)} />
                        <div>
                            <Text strong>{name}</Text>
                            {moreCount > 0 && (
                                <div className="pcu-font-12 pcu-color-muted">+ {moreCount} {t("more_items", "more items")}</div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: t("order_id", "Order ID"),
            key: "orderId",
            render: (_, record) => <Text strong>#{record.order?.orderId || "—"}</Text>,
        },
        {
            title: t("phone", "Phone"),
            key: "phone",
            render: (_, record) => (
                <Text>{record.order?.deliveryAddress?.phone || record.order?.shippingAddress?.phone || record.order?.user?.phone || "—"}</Text>
            ),
        },
        {
            title: t("date", "Date"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => <Text className="pcu-font-12">{formatDate(date)}</Text>,
        },
        {
            title: t("status", "Status"),
            dataIndex: "status",
            key: "status",
            render: (status) => <StatusTag status={status} />,
        },
    ];

    const MobileCard = ({ req }) => {
        const displayItems = req.returnItems && req.returnItems.length > 0 ? req.returnItems : (req.items || []);
        const firstItem = displayItems[0];
        const name = firstItem?.productName || firstItem?.name || firstItem?.product?.name || "—";
        const img = firstItem?.productImage || firstItem?.imageUrl || firstItem?.product?.imageUrl;
        return (
            <Card
                size="small"
                className="pcu-radius-8 pcu-shadow-sm"
                style={{ cursor: "pointer", marginBottom: 10 }}
                onClick={() => handleViewDetails(req)}
            >
                <div className="pcu-flex pcu-justify-between pcu-items-start pcu-margin-bottom-12">
                    <div className="pcu-flex-center-y pcu-gap-10">
                        <Avatar shape="square" size={48} src={getImageUrl(img)} />
                        <div>
                            <Text strong className="pcu-font-13">
                                {name}
                            </Text>
                            <div>
                                <Text type="secondary" className="pcu-font-11">
                                    #{req.order?.orderId || "—"}
                                </Text>
                            </div>
                        </div>
                    </div>
                    <StatusTag status={req.status} />
                </div>
                <Divider className="pcu-margin-y-8" />
                <div className="pcu-flex pcu-justify-between">
                    <div>
                        <Text type="secondary" className="pcu-font-11 pcu-display-block">{t("reason", "Reason")}</Text>
                        <Text className="pcu-font-12">{req.reason || firstItem?.returnReason || "—"}</Text>
                    </div>
                    <div className="pcu-text-right">
                        <Text type="secondary" className="pcu-font-11 pcu-display-block">{t("phone", "Phone")}</Text>
                        <Text className="pcu-font-12">{req.order?.deliveryAddress?.phone || req.order?.shippingAddress?.phone || req.order?.user?.phone || "—"}</Text>
                    </div>
                </div>
                <div className="pcu-margin-top-8">
                    <Text type="secondary" className="pcu-font-11">{formatDate(req.createdAt)}</Text>
                </div>
            </Card>
        );
    };

    return (
        <div className="sd-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <SwapOutlined style={{ fontSize: '22px', color: '#3b82f6' }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>
                            {t("return_requests", "Return Requests")}
                        </h1>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>
                            {requests.length} {t("requests_found", "requests total")}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchRequests} 
                        loading={loading}
                        style={{
                            height: '40px',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            color: '#475569',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        {t("refresh", "Refresh")}
                    </Button>
                </div>
            </div>

            <div className="pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden" style={{ border: '1px solid #f1f5f9' }}>
                <div className="pcu-padding-24">
                {loading ? (
                    <div className="pcu-flex-center pcu-padding-y-100">
                        <Spin size="large" />
                    </div>
                ) : requests.length === 0 ? (
                    <EmptyState
                        title={t("no_return_requests", "No Return Requests")}
                        description={t(
                            "no_return_requests_desc",
                            "When you submit a return request, it will appear here with its current status."
                        )}
                        gradient="orange"
                        showAction={false}
                        icon="inbox"
                    />
                ) : isMobile ? (
                    <div>
                        {requests.map((req) => (
                            <MobileCard key={req._id} req={req} />
                        ))}
                    </div>
                ) : (
                    <div className="pcu-border-top pcu-margin-x-n24 pcu-margin-top-16">
                        <DataTable
                            columns={columns}
                            data={requests}
                            rowKey="_id"
                            loading={loading}
                            pagination={{ 
                                position: ["bottomRight"], 
                                pageSize: 10,
                                onChange: (page) => {
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                            onRow={(record) => ({
                                onClick: () => handleViewDetails(record),
                                style: { cursor: "pointer" },
                            })}
                        />
                    </div>
                )}
            </div>

            {/* Details Modal */}
            <Modal
                title={
                    <span>
                        <SwapOutlined style={{ color: "#3b82f6", marginRight: 8 }} />
                        {t("return_details", "Return Details")}
                        {selectedRequest && ` — #${selectedRequest.returnId}`}
                    </span>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        {t("close", "Close")}
                    </Button>,
                ]}
                width={680}
            >
                {selectedRequest && (
                    <>
                        {/* Status Banner */}
                        <Card
                            size="small"
                            style={{
                                marginBottom: 20,
                                background: "rgba(59,130,246,0.05)",
                                borderColor: "#bae7ff",
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <SwapOutlined style={{ fontSize: 22, color: "#3b82f6", marginTop: 2 }} />
                                <div style={{ flex: 1 }}>
                                    <Text strong>
                                        {t("return_status", "Return Status")}:{" "}
                                        <StatusTag status={selectedRequest.status} />
                                    </Text>
                                    {selectedRequest.sellerComment && (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                background: "#fff",
                                                padding: "8px 10px",
                                                borderRadius: 6,
                                            }}
                                        >
                                            <Text strong className="pcu-font-12">
                                                {selectedRequest.sellerComment.includes('(Admin Action)') ? "Admin Comment" : t("seller_comment", "Seller Comment")}:
                                            </Text>{" "}
                                            <Text className="pcu-font-12">
                                                {selectedRequest.sellerComment.includes('(Admin Action)') 
                                                    ? selectedRequest.sellerComment.replace('(Admin Action)', '').trim() 
                                                    : selectedRequest.sellerComment}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {selectedRequest.images && selectedRequest.images.length > 0 && (
                            <div className="pcu-margin-bottom-20">
                                <Divider orientation="left" style={{ marginTop: 0 }}>{t("evidence_images", "Evidence Images")}</Divider>
                                <div className="pcu-flex pcu-gap-8 pcu-flex-wrap">
                                    {selectedRequest.images.map((img, idx) => (
                                        <div key={idx} style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                            <img src={getImageUrl(img)} alt={`evidence-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
                            <Descriptions.Item label={t("order_id", "Order ID")} span={2}>
                                #{selectedRequest.order?.orderId || "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("request_id", "Request ID")}>
                                {selectedRequest.returnId}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("refund_value", "Refund Amount")} span={2}>
                                <Text strong className="pcu-color-orange">
                                    {formatPrice(selectedRequest.refundAmount)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("submitted", "Submitted")} span={3}>
                                {formatDateTime(selectedRequest.createdAt)}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">{t("requested_items", "Requested Items")}</Divider>
                        <List
                            itemLayout="horizontal"
                            dataSource={
                                selectedRequest.returnItems && selectedRequest.returnItems.length > 0
                                    ? selectedRequest.returnItems.map(item => ({
                                        name: item.productName,
                                        imageUrl: item.productImage,
                                        quantity: item.returnQty,
                                        price: item.price,
                                        returnReason: item.returnReason,
                                        description: item.description
                                      }))
                                    : (selectedRequest.items || []).map(item => ({
                                        name: item.name || item.product?.name,
                                        imageUrl: item.imageUrl || item.product?.imageUrl,
                                        quantity: item.quantity,
                                        price: item.price,
                                        returnReason: selectedRequest.reason,
                                        description: selectedRequest.description
                                      }))
                            }
                            renderItem={(item) => (
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                shape="square"
                                                size={56}
                                                src={getImageUrl(item.imageUrl)}
                                            />
                                        }
                                        title={item.name}
                                        description={
                                            <div>
                                                <div style={{ marginBottom: 4 }}>
                                                    <Text type="secondary" className="pcu-font-12">
                                                        Shop: <span style={{ color: "var(--pcu-nearzon-orange, #f97316)", fontWeight: 500 }}>{selectedRequest.shop?.name || selectedRequest.order?.shop?.name || "Nearzon Seller"}</span>
                                                    </Text>
                                                </div>
                                                <div style={{ marginBottom: 4 }}>
                                                    <Text className="pcu-font-12" type="secondary">
                                                        {t("reason", "Reason")}: {item.returnReason || "—"}
                                                    </Text>
                                                </div>
                                                {item.description && (
                                                    <div style={{ marginBottom: 4 }}>
                                                        <Text className="pcu-font-12" type="secondary">
                                                            {t("description", "Description")}: {item.description}
                                                        </Text>
                                                    </div>
                                                )}
                                                {`${t("quantity", "Qty")}: ${item.quantity}`}
                                            </div>
                                        }
                                    />
                                    <Text strong>{formatPrice(item.price * item.quantity)}</Text>
                                </List.Item>
                            )}
                        />
                    </>
                )}
            </Modal>
        </div>
        </div>
    );
};

export default BuyerReturnRequests;
